/* eslint-disable react-hooks/exhaustive-deps */
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Col, Form, Row, Typography } from "antd";
import CustomCard from "components/CustomCard";
import CustomModal from "components/CustomModal";
import CustomTable from "components/CustomTable";
import CustomTextHiddenLine from "components/CustomTextHiddenLine";
import FormInput from "components/FormInput";
import UploadFiles from "components/UploadFiles";
import { INPUT_TYPE, REGEX } from "config/constants";
import { optionsCauseReject, styleHeaderCard, styleTitleCard } from "features/Purchase/constant";
import { useConfirmRejectCheckInfoPurchaseOrder } from "hooks/purchase";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getFile, notify } from "utils/helperFuncs";
import "./index.scss";

export const ButtonRejectCheckInfo = ({ record, visible, setVisible, visibleReject, setVisibleReject }) => {
  const [form] = Form.useForm();
  const [receivedQuantity, setReceivedQuantity] = useState([]);
  const [notQualifiedQuantity, setNotQualifiedQuantity] = useState([]);

  const [loading, setLoading] = useState(false);

  const handleCancel = () => {
    setVisibleReject(false);
  };

  const { handleConfirmRejectCheckInfoPurchaseOrder } = useConfirmRejectCheckInfoPurchaseOrder();

  const onReject = async () => {
    try {
      const values = await form.validateFields(null, { force: true });
      setLoading(true);
      await handleConfirmRejectCheckInfoPurchaseOrder({
        id: record?.id,
        reasonInput: {
          reason: values?.reason,
          fileURLs: values?.fileURLs,
        },
        items: values?.items,
      })
        .then(() => {
          setVisible(false);
          setVisibleReject(false);
          setLoading(false);
          notify.success({
            message: t("purchase.buttonGroup.confirmRejectMessageSuccess"),
          });
        })
        .catch((error) => {
          setLoading(false);
          notify.error({
            message: error.message,
          });
        });
    } catch (error) {
      console.error(error);
    }
  };

  const columns = [
    {
      title: <span className="title-column">{t("common.index")}</span>,
      align: "center",
      width: "5%",
      render: (_, _record, index) => index + 1,
    },
    {
      title: <span>{t("warehouse.recommendImport.PARAMETER")}</span>,
      align: "start",
      width: "25%",
      render: (_, record) => {
        return (
          <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Typography.Link strong>
              <Link to={`/product/detail/${record?.product?.id}`} target="_blank">
                <Typography.Link strong>{record?.product?.code}</Typography.Link>
              </Link>
            </Typography.Link>
            <CustomTextHiddenLine line={3} text={record?.product?.name || "--"} />
          </div>
        );
      },
    },
    {
      title: <span>{t("purchase.table.ORDER_QUANTITY")}</span>,
      align: "center",
      width: "10%",
      render: (_, record) => {
        return <span>{record?.quantity}</span>;
      },
    },
    {
      title: <span>{t("purchase.table.ACTUAL_QUANTITY")}</span>,
      align: "center",
      width: "15%",
      render: (_, record, index) => {
        return (
          <FormInput
            {...{
              type: INPUT_TYPE.CURRENCY_INPUT,
              formItemOptions: {
                name: [index, "receivedQuantity"],
                rules: [
                  {
                    required: true,
                    message: t("purchase.rules.pleaseActualQuantity"),
                  },
                  {
                    pattern: REGEX.NUMBER,
                  },
                ],
              },
              inputOptions: {
                maxLength: 4,
                min: 0,
                style: { width: "80px" },
                className: "form-text-center form-border-radius-right",
                placeholder: "0",
              },
            }}
          />
        );
      },
    },
    {
      title: <span>{t("purchase.table.NOT_QUANTITY_QUALIFIED")}</span>,
      align: "center",
      width: "15%",
      render: (_, record, index) => {
        const receivedQuantityFieldName = [index, "receivedQuantity"];
        return (
          <FormInput
            {...{
              type: INPUT_TYPE.CURRENCY_INPUT,
              formItemOptions: {
                name: [index, "notQualifiedQuantity"],
                dependencies: [receivedQuantityFieldName],
                rules: [
                  {
                    required: true,
                    message: t("purchase.rules.pleaseNotQuantityQualified"),
                  },
                  {
                    pattern: REGEX.NUMBER,
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const inputValue = parseFloat(value);
                      if (!isNaN(inputValue) && inputValue > (receivedQuantity[index] || 0)) {
                        return Promise.reject(t("purchase.rules.pleaseNotQuantityQualified2"));
                      }
                      return Promise.resolve();
                    },
                  }),
                ],
              },
              inputOptions: {
                maxLength: 4,
                min: 0,
                style: { width: "80px" },
                className: "form-text-center form-border-radius-right",
                placeholder: "0",
              },
            }}
          />
        );
      },
    },
    {
      title: <span>{t("purchase.table.QUANTITY_QUALIFIED")}</span>,
      align: "center",
      width: "10%",
      render: (_, record, index) => {
        const currentNotQualified = notQualifiedQuantity && notQualifiedQuantity[index];
        const currentQuantity = receivedQuantity && receivedQuantity[index];
        const result =
          currentNotQualified >= 0 && currentQuantity > currentNotQualified ? currentQuantity - currentNotQualified : 0;
        return <Typography.Text>{result}</Typography.Text>;
      },
    },
    {
      title: <span>{t("purchase.table.REASON_REJECT")}</span>,
      align: "start",
      width: "20%",
      maxWidth: "20%",
      render: (_, record, index) => {
        return (
          <div>
            <FormInput
              {...{
                type: INPUT_TYPE.SELECT,
                formItemOptions: {
                  name: [index, "causes"],
                  rules: [
                    {
                      required: true,
                      message: t("purchase.placeHolder.selectReasonReject"),
                    },
                  ],
                },
                inputOptions: {
                  options: optionsCauseReject,
                  maxTagCount: 1,
                  dropdownMatchSelectWidth: false,
                  placeholder: t("purchase.placeHolder.selectReasonReject"),
                  mode: "multiple",
                  style: {
                    width: "100%",
                    fontWeight: "500",
                  },
                },
              }}
            />
          </div>
        );
      },
    },
  ];
  const initialValues = {
    items: [],
    fileURLs: null,
    reason: null,
  };

  const handleValuesChange = (changedValue, allValues) => {
    const calculatedReceivedQuantity = allValues?.items?.map((item) => {
      return item.receivedQuantity;
    });
    const calculatedNotQualifiedQuantity = allValues?.items?.map((item) => {
      return item.notQualifiedQuantity;
    });
    setReceivedQuantity(calculatedReceivedQuantity);
    setNotQualifiedQuantity(calculatedNotQualifiedQuantity);
  };

  useEffect(() => {
    const formData = form.getFieldsValue();
    form.setFieldsValue({
      ...formData,
      items: record?.items?.map((item, index) => ({
        purchaseOrderItemID: item?.id,
        receivedQuantity: formData?.items && formData?.items[index]?.receivedQuantity,
        notQualifiedQuantity: formData?.items && formData?.items[index]?.notQualifiedQuantity,
        causes: formData?.items && formData?.items[index]?.causes,
      })),
    });
  }, [form, record, visibleReject]);

  useEffect(() => {
    form.resetFields();
  }, [visible]);
  return (
    <Form form={form} name="validate_other" initialValues={initialValues} onValuesChange={handleValuesChange}>
      <CustomModal
        {...{
          width: "65%",
          title: (
            <span style={{ fontSize: "18px" }}>
              <Typography.Text strong>{t("purchase.buttonGroup.rejectPurchaseOrder")}</Typography.Text>{" "}
            </span>
          ),
          footer: false,
          centered: true,
          styleButton: "notButton",
          hideConfirmButton: true,
          hideCancelButton: true,
          addMoreButton: (
            <div className="button-group-action-check-info-purchase-order">
              <Button type="secondary" htmlType="submit" onClick={handleCancel} loading={loading}>
                {t("common.comeBack")}
              </Button>
              <Button type="primary" htmlType="submit" onClick={onReject} loading={loading}>
                {t("common.confirm")}
              </Button>
            </div>
          ),
          visible: visibleReject,
          onCancel: handleCancel,
          className: "button-reject-check-info-purchase-order",
        }}
      >
        <div className="form-check-info-purchase-order">
          <CustomCard
            headStyle={styleHeaderCard}
            title={
              <Typography.Text style={styleTitleCard} strong>
                {t("purchase.buttonGroup.LIST_PRODUCT")}
              </Typography.Text>
            }
            content={
              <>
                <div className="message-extra-reject">
                  <ExclamationCircleOutlined /> {t("purchase.buttonGroup.extraReject")}
                </div>
                <Form.List name="items">
                  {(items, { remove }) => {
                    return <CustomTable columns={columns} dataSource={record?.items} />;
                  }}
                </Form.List>
              </>
            }
          />
          <Row gutter={[16]} className="form-bottom">
            <Col span={13}>
              <FormInput
                type={INPUT_TYPE.TEXT_AREA}
                formItemOptions={{
                  label: <b>{t("warehouse.recommendImport.reasonReject")}</b>,
                  labelCol: { span: 24, offset: 0 },
                  name: "reason",
                }}
                inputOptions={{
                  rows: 4,
                  placeholder: t("purchase.placeHolder.pleaseReasonReject"),
                }}
              />
            </Col>
            <Col span={11}>
              <Form.Item
                labelCol={{ span: 24, offset: 0 }}
                name={"fileURLs"}
                getValueFromEvent={getFile}
                valuePropName={"fileList"}
                label={<Typography.Text strong>{t("warrantyRequest.popup.files")}</Typography.Text>}
                extra={t("warrantyRequest.create.noteAttack")}
              >
                <UploadFiles />
              </Form.Item>
            </Col>
          </Row>
        </div>
      </CustomModal>
    </Form>
  );
};
